// Central API client for the Augment HR frontend.
// Reads the JWT from localStorage and attaches it to every request.

const BASE = process.env.REACT_APP_API_URL || "http://localhost:4000/api";

// The browser's IANA timezone (e.g. "Asia/Karachi", "America/New_York").
// Sent with attendance calls so clock-in/out times reflect the user's actual
// location instead of a server-side default.
export const tzName = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
};

const TOKEN_KEY = "augment_hr_token";
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

// Active entity scope (Company → Group → Entity). Sent on every request as the
// X-Entity header (re-authorized server-side). "all" = consolidated view, the
// default. HR can switch it; for employees the server ignores it (they're pinned).
const ENTITY_KEY = "augment_hr_entity";
export const getEntity = () => localStorage.getItem(ENTITY_KEY) || "all";
export const setEntity = (id) => localStorage.setItem(ENTITY_KEY, id || "all");
export const clearEntity = () => localStorage.removeItem(ENTITY_KEY);

async function request(path, { method = "GET", body } = {}) {
  const headers = { "Content-Type": "application/json" };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  headers["X-Entity"] = getEntity();

  const res = await fetch(BASE + path, {
    method,
    headers,
    body: body != null ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401) {
    // Session expired / invalid — drop token so the app returns to login.
    clearToken();
  }

  let data = null;
  const text = await res.text();
  if (text) {
    try { data = JSON.parse(text); } catch { data = text; }
  }
  if (!res.ok) {
    const message = (data && data.error) || `Request failed (${res.status})`;
    throw new Error(message);
  }
  return data;
}

const get = (p) => request(p);
const post = (p, body) => request(p, { method: "POST", body });
const patch = (p, body) => request(p, { method: "PATCH", body });
const put = (p, body) => request(p, { method: "PUT", body });
const del = (p) => request(p, { method: "DELETE" });

// Multipart upload — browser sets Content-Type with boundary automatically
async function upload(path, formData) {
  const headers = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  headers["X-Entity"] = getEntity();
  const res = await fetch(BASE + path, { method: "POST", headers, body: formData });
  if (res.status === 401) clearToken();
  let data = null;
  const text = await res.text();
  if (text) { try { data = JSON.parse(text); } catch { data = text; } }
  if (!res.ok) throw new Error((data && data.error) || `Upload failed (${res.status})`);
  return data;
}

// Binary download — triggers browser save-as dialog
async function downloadBlob(path, fallbackFilename = "document") {
  const headers = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  headers["X-Entity"] = getEntity();
  const res = await fetch(BASE + path, { headers });
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const blob = await res.blob();
  const cd = res.headers.get("Content-Disposition") || "";
  const match = cd.match(/filename="?([^";\n]+)"?/i);
  const filename = match ? decodeURIComponent(match[1]) : fallbackFilename;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export const api = {
  // ---- auth ----
  login: (email, password, code) => post("/auth/login", { email, password, ...(code ? { code } : {}) }),
  mfaEnroll: () => post("/auth/mfa/enroll", {}),               // → { secret, otpauthUrl }
  mfaVerify: (code) => post("/auth/mfa/verify", { code }),
  mfaDisable: (code) => post("/auth/mfa/disable", { code }),
  logout: () => post("/auth/logout", {}),                             // records LOGOUT (token still valid)
  me: () => get("/auth/me"),
  updateProfile: (data) => patch("/auth/me", data),                   // self-service: avatar, … → { user }
  forgotPassword: (email) => post("/auth/forgot", { email }),         // request reset link (public)
  requestPasswordReset: () => post("/auth/request-password-reset", {}), // logged-in: email myself a reset link (SES)
  checkResetToken: (token) => get(`/auth/check-token?token=${encodeURIComponent(token)}`),
  setPassword: (token, password) => post("/auth/set-password", { token, password }),

  // ---- employees ----
  employees: () => get("/employees"),
  employee: (id) => get(`/employees/${id}`),
  employeeFull: (id) => get(`/employees/${id}/full`),
  addEmployee: (data) => post("/employees", data),
  updateEmployee: (id, data) => patch(`/employees/${id}`, data),
  promoteManager: (id) => patch(`/employees/${id}/promote-manager`, {}),         // HR: make manager
  removeManager: (id, newManagerId) => post(`/employees/${id}/remove-manager`, { newManagerId }), // HR: demote + reassign team
  // additional positions (Phase 2)
  positions: (id) => get(`/employees/${id}/positions`),
  addPosition: (id, data) => post(`/employees/${id}/positions`, data),
  updatePosition: (id, posId, data) => patch(`/employees/${id}/positions/${posId}`, data),
  deletePosition: (id, posId) => del(`/employees/${id}/positions/${posId}`),

  // ---- leave ----
  leaves: () => get("/leaves"),
  leavesTeam: () => get("/leaves/team"),                 // manager: their team's requests
  leaveBalances: () => get("/leaves/balances"),
  applyLeave: (data) => post("/leaves", data),
  cancelLeave: (id) => post(`/leaves/${id}/cancel`),
  managerDecideLeave: (id, decision) => patch(`/leaves/${id}/manager-decision`, { decision }),
  decideLeave: (id, decision) => patch(`/leaves/${id}/decision`, { decision }), // HR final

  // ---- attendance ----
  // tz lets the server compute "today" and clock times in the viewer's zone.
  attendanceToday: (date) =>
    get(`/attendance/today?tz=${encodeURIComponent(tzName())}` + (date ? `&date=${date}` : "")),
  attendanceRange: (from, to) =>
    get(`/attendance/range?from=${from}&to=${to}&tz=${encodeURIComponent(tzName())}`),
  attendanceTeam: () => get(`/attendance/team?tz=${encodeURIComponent(tzName())}`), // manager: their team's attendance
  attendanceMe: () => get(`/attendance/me?tz=${encodeURIComponent(tzName())}`),
  clock: (action) => post("/attendance/clock", { action, tz: tzName() }),

  // ---- ATS ----
  jobs: () => get("/ats/jobs"),
  job: (id) => get(`/ats/jobs/${id}`),
  addJob: (data) => post("/ats/jobs", data),
  jdTemplates: () => get("/jd-templates"),
  createJdTemplate: (data) => post("/jd-templates", data),
  updateJdTemplate: (id, data) => patch(`/jd-templates/${id}`, data),
  deleteJdTemplate: (id) => del(`/jd-templates/${id}`),
  advanceJob: (id) => patch(`/ats/jobs/${id}/advance`),
  closeJob: (id) => patch(`/ats/jobs/${id}/close`),
  updateJob: (id, data) => patch(`/ats/jobs/${id}`, data),                       // edit JD + criteria (1b)
  screenJob: (id, pool = true) => get(`/ats/jobs/${id}/screen${pool ? "" : "?pool=0"}`),
  shortlistCandidate: (jobId, candidateId) => post(`/ats/jobs/${jobId}/shortlist`, { candidateId }),

  candidates: () => get("/ats/candidates"),
  candidate: (id) => get(`/ats/candidates/${id}`),
  moveCandidate: (id, dir) => patch(`/ats/candidates/${id}/move`, { dir }),

  // ---- recruitment online tests (4.1) ----
  recruitmentTests: () => get("/recruitment-tests/tests"),
  recruitmentTest: (id) => get(`/recruitment-tests/tests/${id}`),
  createRecruitmentTest: (data) => post("/recruitment-tests/tests", data),
  updateRecruitmentTest: (id, data) => patch(`/recruitment-tests/tests/${id}`, data),
  deleteRecruitmentTest: (id) => del(`/recruitment-tests/tests/${id}`),
  addTestQuestion: (testId, data) => post(`/recruitment-tests/tests/${testId}/questions`, data),
  updateTestQuestion: (testId, qid, data) => patch(`/recruitment-tests/tests/${testId}/questions/${qid}`, data),
  deleteTestQuestion: (testId, qid) => del(`/recruitment-tests/tests/${testId}/questions/${qid}`),
  assignTest: (candidateId, testId) => post(`/recruitment-tests/candidates/${candidateId}/assign`, { testId }),
  candidateAssignments: (candidateId) => get(`/recruitment-tests/candidates/${candidateId}/assignments`),
  deleteTestAssignment: (id) => del(`/recruitment-tests/assignments/${id}`),
  // public (no auth) — token is the credential
  publicAssessment: (token) => get(`/assessment/${encodeURIComponent(token)}`),
  submitAssessment: (token, answers) => post(`/assessment/${encodeURIComponent(token)}/submit`, { answers }),
  rejectCandidate: (id) => patch(`/ats/candidates/${id}/reject`),
  restoreCandidate: (id) => patch(`/ats/candidates/${id}/restore`),
  // talent pool (1a)
  talentPool: (params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v != null && v !== "")).toString();
    return get(`/ats/talent-pool${qs ? `?${qs}` : ""}`);
  },
  addCandidate: (data) => post("/ats/candidates", data),
  uploadCandidateCv: (id, file) => { const fd = new FormData(); fd.append("file", file); return upload(`/ats/candidates/${id}/cv`, fd); },
  downloadCandidateCv: (id, name) => downloadBlob(`/ats/candidates/${id}/cv/download`, name),

  interviews: () => get("/ats/interviews"),
  scheduleInterview: (data) => post("/ats/interviews", data),
  completeInterview: (id, rec) => patch(`/ats/interviews/${id}/complete`, { rec }),
  // interview evaluations (1c)
  openEvaluation: (ivId) => post(`/ats/interviews/${ivId}/evaluation`, {}),
  evaluation: (ivId) => get(`/ats/interviews/${ivId}/evaluation`),
  submitEvaluation: (ivId, data) => patch(`/ats/interviews/${ivId}/evaluation`, data),
  jobEvaluations: (jobId) => get(`/ats/jobs/${jobId}/evaluations`),

  offers: () => get("/ats/offers"),
  createOffer: (candId, salary, start) => post("/ats/offers", { candId, salary, start }),
  advanceOffer: (id) => patch(`/ats/offers/${id}/advance`),
  decideOffer: (id, decision) => patch(`/ats/offers/${id}/decide`, { decision }),
  // offer-via-link (1d)
  sendOfferLink: (id) => post(`/ats/offers/${id}/send-link`, {}),
  publicOffer: (token) => get(`/offer/${token}`),                         // unauthenticated
  decideOfferPublic: (token, decision) => post(`/offer/${token}/decision`, { decision }),

  onboarding: () => get("/ats/onboarding"),
  toggleOnboardTask: (obId, taskId) => patch(`/ats/onboarding/${obId}/task/${taskId}`),
  convertToEmployee: (obId) => patch(`/ats/onboarding/${obId}/convert`),
  // onboarding profile page (1e)
  onboardingProfileLink: (id) => post(`/ats/onboarding/${id}/profile-link`, {}),
  publicOnboarding: (token) => get(`/onboarding/${token}`),               // unauthenticated
  // onboarding feedback surveys (1f)
  onboardingSurveys: (id) => get(`/ats/onboarding/${id}/surveys`),
  completeSurvey: (sid, data) => patch(`/ats/surveys/${sid}`, data),

  // ---- self-service ----
  myPayslips: () => get("/me/payslips"),
  myDocuments: () => get("/me/documents"),
  downloadMyDocument: (docId, name) =>
    downloadBlob(`/me/documents/${docId}/download`, name),
  myGoals: () => get("/me/goals"),

  // ---- employee documents (HR) ----
  employeeDocuments: (empId) => get(`/employees/${empId}/documents`),
  uploadEmployeeDocumentsBulk: (empId, files, type) => {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    if (type) fd.append("type", type);
    return upload(`/employees/${empId}/documents/bulk`, fd);
  },
  downloadEmployeeDocument: (empId, docId, name) =>
    downloadBlob(`/employees/${empId}/documents/${docId}/download`, name),
  deleteEmployeeDocument: (empId, docId) =>
    del(`/employees/${empId}/documents/${docId}`),
  // document hardening (0.5a): metadata patch + new version upload
  updateEmployeeDocument: (empId, docId, data) => patch(`/employees/${empId}/documents/${docId}`, data),
  uploadEmployeeDocumentVersion: (empId, docId, file, opts = {}) => {
    const fd = new FormData();
    fd.append("file", file);
    if (opts.name) fd.append("name", opts.name);
    if (opts.expiresAt) fd.append("expiresAt", opts.expiresAt);
    return upload(`/employees/${empId}/documents/${docId}/version`, fd);
  },
  employeeDocumentHistory: (empId, docId) => get(`/employees/${empId}/documents/${docId}/history`),

  // ---- policies ----
  policies: () => get("/policies"),
  ackPolicy: (id) => post(`/policies/${id}/ack`),
  createOrgDoc: (name, cat, file) => {
    const fd = new FormData();
    fd.append("name", name);
    fd.append("cat", cat || "General");
    fd.append("kind", "Form");
    if (file) fd.append("file", file);
    return upload("/policies", fd);
  },
  createPolicy: (name, cat, file) => {
    const fd = new FormData();
    fd.append("name", name);
    fd.append("cat", cat || "General");
    fd.append("kind", "Policy");
    if (file) fd.append("file", file);
    return upload("/policies", fd);
  },
  bulkCreateOrgDocs: (files, cat) => {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    if (cat) fd.append("cat", cat);
    return upload("/policies/bulk-org-docs", fd);
  },
  downloadPolicy: (id, name) => downloadBlob(`/policies/${id}/download`, name),
  updatePolicy: (id, data) => patch(`/policies/${id}`, data), // edit title + category
  deletePolicy: (id) => del(`/policies/${id}`),

  // ---- salary structure (3a) ----
  myStructure: () => get("/payroll/me/structure"),
  empStructure: (id) => get(`/payroll/structure/${id}`),
  saveStructure: (id, lines) => put(`/payroll/structure/${id}`, { lines }),
  // pay adjustments (allowances / per-diems / deductions)
  myAdjustments: () => get("/payroll/me/adjustments"),
  payAdjustments: (id) => get(`/payroll/adjustments/${id}`),
  addPayAdjustment: (id, data) => post(`/payroll/adjustments/${id}`, data),
  updatePayAdjustment: (adjId, data) => patch(`/payroll/adjustments/${adjId}`, data),
  deletePayAdjustment: (adjId) => del(`/payroll/adjustments/${adjId}`),

  // ---- itemized payslip (B1) ----
  payslipDetail: (id) => get(`/payroll/payslips/${id}`),

  // ---- final settlement (3b) ----
  mySettlement: () => get("/payroll/me/settlement"),
  empSettlement: (id) => get(`/payroll/settlement/${id}`),

  // ---- bank export (3c) ----
  bankExport: () => get("/payroll/bank-export"),

  // ---- statutory registers (3f) ----
  statutoryReport: () => get("/payroll/statutory-report"),

  // ---- overtime (3d) ----
  myOvertime: (month) => get(`/attendance/overtime${month ? `?month=${month}` : ""}`),
  empOvertime: (id, month) => get(`/attendance/overtime/${id}${month ? `?month=${month}` : ""}`),

  // ---- MSS team analytics (2e) ----
  teamAnalytics: () => get("/team/analytics"),

  // ---- loans & advances ----
  myLoans: () => get("/loans/mine"),
  requestLoan: (data) => post("/loans", data),
  loansAll: () => get("/loans"),

  // ---- succession & talent pipeline ----
  succession: () => get("/succession"),
  createSuccessionPlan: (data) => post("/succession", data),
  updateSuccessionPlan: (id, data) => patch(`/succession/${id}`, data),
  deleteSuccessionPlan: (id) => del(`/succession/${id}`),
  addSuccessor: (id, data) => post(`/succession/${id}/successors`, data),
  removeSuccessor: (id, sid) => del(`/succession/${id}/successors/${sid}`),

  // ---- disciplinary actions ----
  myDisciplinary: () => get("/disciplinary/mine"),
  disciplinaryAll: () => get("/disciplinary"),
  issueDisciplinary: (data) => post("/disciplinary", data),
  updateDisciplinary: (id, data) => patch(`/disciplinary/${id}`, data),

  // ---- offboarding / end-of-service ----
  offboardings: () => get("/offboarding"),
  offboarding: (id) => get(`/offboarding/${id}`),
  initiateOffboarding: (data) => post("/offboarding", data),
  toggleClearanceTask: (id, taskId, done) => patch(`/offboarding/${id}/tasks/${taskId}`, { done }),
  settleOffboarding: (id) => post(`/offboarding/${id}/settle`),

  // ---- benefits administration ----
  myBenefits: () => get("/benefits/me"),
  setBenefit: (planId, data) => post(`/benefits/me/${planId}`, data),
  benefitPlans: () => get("/benefits/plans"),
  createBenefitPlan: (data) => post("/benefits/plans", data),
  updateBenefitPlan: (id, data) => patch(`/benefits/plans/${id}`, data),
  deleteBenefitPlan: (id) => del(`/benefits/plans/${id}`),
  enrollBenefit: (id, employeeIds, coverage) => post(`/benefits/plans/${id}/enroll`, { employeeIds, coverage }),

  // ---- health & safety incidents ----
  incidentTypes: () => get("/incidents/types"),
  myIncidents: () => get("/incidents/mine"),
  reportIncident: (data) => post("/incidents", data),
  incidentsAll: () => get("/incidents"),
  updateIncident: (id, data) => patch(`/incidents/${id}`, data),
  // ---- H&S depth (6): claims, rehab, carbon ----
  safetyClaims: () => get("/safety/claims"),
  myClaims: () => get("/safety/me/claims"),
  fileClaim: (data) => post("/safety/claims", data),
  updateClaim: (id, data) => patch(`/safety/claims/${id}`, data),
  rehabPlans: () => get("/safety/rehab"),
  myRehab: () => get("/safety/me/rehab"),
  createRehab: (data) => post("/safety/rehab", data),
  updateRehab: (id, data) => patch(`/safety/rehab/${id}`, data),
  deleteRehab: (id) => del(`/safety/rehab/${id}`),
  carbonEntries: () => get("/safety/carbon"),
  carbonFactors: () => get("/safety/carbon/factors"),
  addCarbon: (data) => post("/safety/carbon", data),
  updateCarbon: (id, data) => patch(`/safety/carbon/${id}`, data),
  deleteCarbon: (id) => del(`/safety/carbon/${id}`),

  // ---- internal comms / announcements ----
  announcementFeed: () => get("/announcements/feed"),
  ackAnnouncement: (id) => post(`/announcements/${id}/ack`),
  likeAnnouncement: (id) => post(`/announcements/${id}/like`),
  announcementsAll: () => get("/announcements"),
  createAnnouncement: (data) => post("/announcements", data),
  updateAnnouncement: (id, data) => patch(`/announcements/${id}`, data),
  deleteAnnouncement: (id) => del(`/announcements/${id}`),
  // events calendar + celebrations (1.6)
  events: () => get("/events"),
  createEvent: (data) => post("/events", data),
  updateEvent: (id, data) => patch(`/events/${id}`, data),
  deleteEvent: (id) => del(`/events/${id}`),
  celebrations: (days = 30) => get(`/events/celebrations?days=${days}`),

  // ---- cross-module insights (6a) ----
  insightsOverview: () => get("/analytics/overview"),
  slaOverview: () => get("/analytics/sla"),
  outcomesReport: () => get("/analytics/outcomes"),
  workforceAnalytics: () => get("/analytics/workforce"),
  departmentAnalytics: () => get("/analytics/departments"),
  recruitmentAnalytics: () => get("/analytics/recruitment"),

  // ---- employee relations / cases (5c) ----
  myCases: () => get("/cases/mine"),
  raiseCase: (data) => post("/cases", data),
  casesAll: () => get("/cases"),
  updateCase: (id, data) => patch(`/cases/${id}`, data),

  // ---- engagement polls (5b) ----
  openPolls: () => get("/polls/open"),
  votePoll: (id, choice) => post(`/polls/${id}/vote`, { choice }),
  pollsAll: () => get("/polls"),
  createPoll: (data) => post("/polls", data),
  closePoll: (id) => post(`/polls/${id}/close`),

  // ---- peer recognition (5a) ----
  recognitionCategories: () => get("/recognition/categories"),
  recognitionWall: () => get("/recognition/wall"),
  recognitionMe: () => get("/recognition/me"),
  recognitionLeaderboard: () => get("/recognition/leaderboard"),
  giveRecognition: (data) => post("/recognition", data),
  // ---- awards (5b) ----
  awardCategories: () => get("/awards/categories"),
  awardDirectory: () => get("/awards/directory"),
  myAwards: () => get("/awards/mine"),
  awardsAll: () => get("/awards"),
  nominateAward: (data) => post("/awards", data),

  // ---- 360 feedback (4c) ----
  myFeedbackRequests: () => get("/feedback/me/requests"),
  submitFeedback: (id, data) => post(`/feedback/requests/${id}`, data),
  feedbackCycles: () => get("/feedback/cycles"),
  feedbackCycle: (id) => get(`/feedback/cycles/${id}`),
  openFeedbackCycle: (data) => post("/feedback/cycles", data),
  closeFeedbackCycle: (id) => post(`/feedback/cycles/${id}/close`),

  // ---- competency / skill-gap (4b) ----
  myCompetencies: () => get("/competencies/me"),
  competencyCatalog: () => get("/competencies/catalog"),
  addCompetency: (data) => post("/competencies/catalog", data),
  updateCompetency: (id, data) => patch(`/competencies/catalog/${id}`, data),
  deleteCompetency: (id) => del(`/competencies/catalog/${id}`),
  employeeCompetencies: (id) => get(`/competencies/employee/${id}`),
  assessCompetencies: (id, assessments) => put(`/competencies/employee/${id}`, { assessments }),

  // ---- training / LMS (4a) ----
  courses: () => get("/training/courses"),
  addCourse: (data) => post("/training/courses", data),
  updateCourse: (id, data) => patch(`/training/courses/${id}`, data),
  deleteCourse: (id) => del(`/training/courses/${id}`),
  courseEnrollments: (id) => get(`/training/courses/${id}/enrollments`),
  enrollEmployees: (id, employeeIds) => post(`/training/courses/${id}/enroll`, { employeeIds }),
  myCourses: () => get("/training/me/courses"),
  updateMyProgress: (id, progress) => patch(`/training/me/courses/${id}`, { progress }),
  courseCertificate: (enrollmentId) => get(`/training/certificate/${enrollmentId}`),
  // training calendar + instructors + self-registration (3.x)
  instructors: () => get("/training/instructors"),
  createInstructor: (data) => post("/training/instructors", data),
  updateInstructor: (id, data) => patch(`/training/instructors/${id}`, data),
  deleteInstructor: (id) => del(`/training/instructors/${id}`),
  trainingSessions: () => get("/training/sessions"),
  createTrainingSession: (data) => post("/training/sessions", data),
  updateTrainingSession: (id, data) => patch(`/training/sessions/${id}`, data),
  deleteTrainingSession: (id) => del(`/training/sessions/${id}`),
  markSessionAttendance: (sessionId, regId, status) => patch(`/training/sessions/${sessionId}/registrations/${regId}`, { status }),
  mySessions: () => get("/training/me/sessions"),
  registerSession: (id) => post(`/training/sessions/${id}/register`, {}),
  unregisterSession: (id) => post(`/training/sessions/${id}/unregister`, {}),

  // ---- org chart + cost centers (2d) ----
  orgReporting: () => get("/org/reporting"),
  orgSnapshots: () => get("/org/snapshots"),
  orgSnapshot: (id) => get(`/org/snapshots/${id}`),
  captureOrgSnapshot: (name) => post("/org/snapshots", { name }),
  deleteOrgSnapshot: (id) => del(`/org/snapshots/${id}`),
  // org restructuring (1.1): live drag-drop reassign + what-if scenarios
  reassignManager: (employeeId, managerId) => post("/org/reassign", { employeeId, managerId }),
  reorgScenarios: () => get("/org/scenarios"),
  reorgScenario: (id) => get(`/org/scenarios/${id}`),
  createReorgScenario: (name) => post("/org/scenarios", { name }),
  setReorgMove: (id, employeeId, managerId) => put(`/org/scenarios/${id}/moves`, { employeeId, managerId }),
  removeReorgMove: (id, employeeId) => del(`/org/scenarios/${id}/moves/${employeeId}`),
  applyReorgScenario: (id) => post(`/org/scenarios/${id}/apply`, {}),
  deleteReorgScenario: (id) => del(`/org/scenarios/${id}`),
  costCenters: () => get("/cost-centers"),
  addCostCenter: (data) => post("/cost-centers", data),
  updateCostCenter: (id, data) => patch(`/cost-centers/${id}`, data),
  deleteCostCenter: (id) => del(`/cost-centers/${id}`),

  // ---- extended profile (2c) ----
  myProfile: () => get("/me/profile"),
  addBank: (data) => post("/me/profile/bank", data),
  updateBank: (id, data) => patch(`/me/profile/bank/${id}`, data),
  deleteBank: (id) => del(`/me/profile/bank/${id}`),
  addDependent: (data) => post("/me/profile/dependents", data),
  deleteDependent: (id) => del(`/me/profile/dependents/${id}`),
  setSignature: (dataUrl) => put("/me/profile/signature", { dataUrl }),
  clearSignature: () => del("/me/profile/signature"),

  // ---- ESS requests + out-of-office (2b) ----
  myRequests: () => get("/requests/mine"),
  submitRequest: (data) => post("/requests", data),
  requestsAll: () => get("/requests"),
  getOoo: () => get("/requests/ooo"),
  setOoo: (data) => put("/requests/ooo", data),
  clearOoo: () => del("/requests/ooo"),

  // ---- HR letters (2a) ----
  letterTypes: () => get("/letters/types"),
  myLetters: () => get("/letters/mine"),
  requestLetter: (data) => post("/letters", data),
  lettersAll: () => get("/letters"),
  letter: (id) => get(`/letters/${id}`),
  previewLetter: (id) => get(`/letters/${id}/preview`),
  issueLetter: (id, body) => post(`/letters/${id}/issue`, { body }),
  rejectLetter: (id) => post(`/letters/${id}/reject`),

  // ---- dashboard + payroll ----
  dashboard: () => get("/dashboard"),
  payroll: (convertTo) => get(`/payroll${convertTo ? `?convertTo=${encodeURIComponent(convertTo)}` : ""}`),
  exchangeRates: () => get("/config/exchange-rates"),
  addExchangeRate: (data) => post("/config/exchange-rates", data),
  updateExchangeRate: (id, rate) => patch(`/config/exchange-rates/${id}`, { rate }),
  deleteExchangeRate: (id) => del(`/config/exchange-rates/${id}`),
  // integrations (5) — channel status + master toggle / provider selection
  integrations: () => get("/config/integrations"),
  updateIntegration: (type, data) => put(`/config/integrations/${type}`, data),
  payrollRuns: () => get("/payroll/runs"),
  approvePayrollRun: (id) => post(`/payroll/runs/${id}/approve`, {}),
  rejectPayrollRun: (id) => post(`/payroll/runs/${id}/reject`, {}),
  payrollBenefits: () => get("/payroll/benefits"),
  payrollTax: () => get("/payroll/tax"),
  payrollMe: () => get("/payroll/me"),
  runPayroll: () => post("/payroll/run"),

  // ---- OKRs / performance ----
  okrsAll: () => get("/okrs"),                                    // HR: every OKR
  okrsMe: () => get("/okrs/me"),                                  // employee: own
  okrsTeam: () => get("/okrs/team"),                             // manager: reports'
  createOkr: (data) => post("/okrs", data),                      // manager assigns
  // ---- performance cycles (4) ----
  performanceCycles: () => get("/performance/cycles"),
  openPerformanceCycles: () => get("/performance/cycles/open"),
  createPerformanceCycle: (data) => post("/performance/cycles", data),
  updatePerformanceCycle: (id, data) => patch(`/performance/cycles/${id}`, data),
  deletePerformanceCycle: (id) => del(`/performance/cycles/${id}`),
  // ---- performance → compensation linkage (1.2) ----
  compRecommendations: () => get("/performance/comp"),
  suggestComp: (employeeId, cycle) => get(`/performance/comp/suggest/${employeeId}?cycle=${encodeURIComponent(cycle || "")}`),
  createComp: (data) => post("/performance/comp", data),
  updateComp: (id, data) => patch(`/performance/comp/${id}`, data),
  approveComp: (id) => post(`/performance/comp/${id}/approve`, {}),
  rejectComp: (id) => post(`/performance/comp/${id}/reject`, {}),
  deleteComp: (id) => del(`/performance/comp/${id}`),
  updateOkrProgress: (id, data) => patch(`/okrs/${id}/progress`, data),
  acceptOkr: (id) => patch(`/okrs/${id}/accept`, {}),            // employee accepts
  submitOkr: (id, data) => patch(`/okrs/${id}/submit`, data),    // employee submits
  reviewOkr: (id, data) => patch(`/okrs/${id}/review`, data),    // manager reviews
  finalizeOkr: (id, data) => patch(`/okrs/${id}/finalize`, data), // HR signs off
  objectOkr: (id, reason) => post(`/okrs/${id}/object`, { reason }),                 // employee appeals a finalized objective
  resolveOkrObjection: (id, data) => post(`/okrs/${id}/resolve-objection`, data),    // HR resolves an appeal
  deleteOkr: (id) => del(`/okrs/${id}`),
  // OKR proof documents (GCS): employee uploads, manager/HR view
  uploadOkrEvidence: (id, files) => {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    return upload(`/okrs/${id}/evidence`, fd);
  },
  downloadOkrEvidence: (id, evId, name) =>
    downloadBlob(`/okrs/${id}/evidence/${evId}/download`, name),
  deleteOkrEvidence: (id, evId) => del(`/okrs/${id}/evidence/${evId}`),

  // ---- notifications ----
  notifications: () => get("/notifications"),                    // { items, unread }
  markNotifRead: (id) => patch(`/notifications/${id}/read`, {}),
  markAllNotifRead: () => post("/notifications/read-all", {}),

  // ---- activity log (HR only) ----
  // params: { actorId, role, module, action, from, to, limit, offset } → { rows, total, limit, offset }
  activity: (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v != null && v !== "")
    ).toString();
    return get(`/activity${qs ? `?${qs}` : ""}`);
  },
  activityFilters: () => get("/activity/filters"),               // { actions, modules, actors }

  // ---- approvals (workflow engine) ----
  approvalsInbox: () => get("/approvals/inbox"),                 // requests where I'm the current approver
  approval: (id) => get(`/approvals/${id}`),                     // detail: steps timeline + action history
  actApproval: (id, decision, comment) => post(`/approvals/${id}/act`, { decision, comment }),
  bulkActApprovals: (ids, decision, comment) => post(`/approvals/bulk-act`, { ids, decision, comment }),
  commentApproval: (id, comment) => post(`/approvals/${id}/comment`, { comment }),
  objectApproval: (id, reason) => post(`/approvals/${id}/object`, { reason }),

  // ---- configuration (leave types + holiday calendars) ----
  leaveTypes: () => get("/config/leave-types"),                  // HR: admin list
  availableLeaveTypes: () => get("/config/leave-types/available"), // current user's eligible types (apply form)
  createLeaveType: (data) => post("/config/leave-types", data),
  updateLeaveType: (id, data) => patch(`/config/leave-types/${id}`, data),
  deleteLeaveType: (id) => del(`/config/leave-types/${id}`),
  overtimeRules: () => get("/config/overtime-rules"),
  createOvertimeRule: (data) => post("/config/overtime-rules", data),
  updateOvertimeRule: (id, data) => patch(`/config/overtime-rules/${id}`, data),
  deleteOvertimeRule: (id) => del(`/config/overtime-rules/${id}`),
  holidayCalendars: () => get("/config/holiday-calendars"),
  holidayCalendar: (id) => get(`/config/holiday-calendars/${id}`),
  createHolidayCalendar: (data) => post("/config/holiday-calendars", data),
  updateHolidayCalendar: (id, data) => patch(`/config/holiday-calendars/${id}`, data),
  deleteHolidayCalendar: (id) => del(`/config/holiday-calendars/${id}`),
  addHoliday: (calId, date, name) => post(`/config/holiday-calendars/${calId}/holidays`, { date, name }),
  removeHoliday: (calId, hid) => del(`/config/holiday-calendars/${calId}/holidays/${hid}`),
  workingDays: (from, to) => get(`/config/working-days?from=${from}&to=${to}`), // chargeable-days preview
  // payroll config (0.2b)
  payrollComponents: () => get("/config/payroll-components"),
  createPayrollComponent: (data) => post("/config/payroll-components", data),
  updatePayrollComponent: (id, data) => patch(`/config/payroll-components/${id}`, data),
  deletePayrollComponent: (id) => del(`/config/payroll-components/${id}`),
  taxSchemes: () => get("/config/tax-schemes"),
  createTaxScheme: (data) => post("/config/tax-schemes", data),
  updateTaxScheme: (id, data) => patch(`/config/tax-schemes/${id}`, data),
  deleteTaxScheme: (id) => del(`/config/tax-schemes/${id}`),
  // manpower planning (1.1) — pay grades + headcount budgets + report
  payGrades: () => get("/config/pay-grades"),
  createPayGrade: (data) => post("/config/pay-grades", data),
  updatePayGrade: (id, data) => patch(`/config/pay-grades/${id}`, data),
  deletePayGrade: (id) => del(`/config/pay-grades/${id}`),
  headcountBudgets: () => get("/config/headcount-budgets"),
  createHeadcountBudget: (data) => post("/config/headcount-budgets", data),
  updateHeadcountBudget: (id, data) => patch(`/config/headcount-budgets/${id}`, data),
  deleteHeadcountBudget: (id) => del(`/config/headcount-budgets/${id}`),
  manpower: () => get("/config/manpower"),
  payGradeCheck: (employeeId) => get(`/config/pay-grades/check/${employeeId}`),
  // approval hierarchies (0.2c)
  workflows: () => get("/config/workflows"),
  updateWorkflow: (key, data) => patch(`/config/workflows/${key}`, data),
  // roles & access (0.3)
  roles: () => get("/config/roles"),
  roleGrants: () => get("/config/role-grants"),
  grantRole: (data) => post("/config/role-grants", data),
  revokeRole: (id) => del(`/config/role-grants/${id}`),
  // agreements: consents/NDA + e-signature (0.5b)
  myConsents: () => get("/agreements/consents/me"),
  signConsent: (id, signatureName) => post(`/agreements/consents/${id}/sign`, { signatureName }),
  declineConsent: (id) => post(`/agreements/consents/${id}/decline`),
  consents: () => get("/agreements/consents"),
  issueConsent: (data) => post("/agreements/consents", data),
  signatureRequests: () => get("/agreements/signatures"),
  sendSignature: (data) => post("/agreements/signatures", data),
  updateSignature: (id, status) => patch(`/agreements/signatures/${id}`, { status }),
  // baseline metrics (0.6)
  baseline: () => get("/config/baseline"),
  addBaseline: (data) => post("/config/baseline", data),
  updateBaseline: (id, data) => patch(`/config/baseline/${id}`, data),
  deleteBaseline: (id) => del(`/config/baseline/${id}`),

  // ---- org (Company → Group → Entity) ----
  orgTree: () => get("/org/tree"),                               // company → groups → entities (switcher)
  orgEntities: () => get("/org/entities"),                       // flat entity list
  createGroup: (data) => post("/org/groups", data),             // HR: { companyId, name, code }
  createEntity: (data) => post("/org/entities", data),          // HR: { groupId, name, code, type }
  updateEntity: (id, data) => patch(`/org/entities/${id}`, data), // HR: { name?, type?, status? }
  // Record a browser-generated download (payslip / tax certificate / payroll
  // export). Fire-and-forget — never blocks or fails the actual download.
  trackDownload: (action, targetLabel) =>
    post("/activity/track", { action, targetLabel }).catch(() => {}),
};
